import React, { useCallback, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Modal } from "reactstrap";
import { ModalBody } from "reactstrap";
import Text from "components/Text";
import styles from "./AddStaff.module.scss";
import crossIcon from "assets/images/cross.svg";
import {
  addDepartmentName,
  deleteDepartmentName,
  editDepartmentName,
  useGetDepartmentListByOwnerId,
} from "repositories/staff-repository";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import Empty from "components/Empty";
import Input from "components/Input";
import icon_tick from "assets/images/icon_tick.svg";
import edit_icon from "assets/images/edit-icon.svg";
import delete_icon from "assets/images/delete-icon.svg";
import plus from "assets/images/plus-icon-outline.svg";
function DepartmentSelectionModal({
  t,
  isModalOpen,
  setIsModalOpen,
  selectedOwnerId,
  preSelectedDepartement,
  setSelectedDepartment,
  updateState,
  fieldValues,
}) {
  const [selectedVal, setSelectedVal] = useState(
    preSelectedDepartement || null
  );
  const [error, setError] = useState("");
  const { data, isLoading, isFetching, refetch } =
    useGetDepartmentListByOwnerId(selectedOwnerId, {
      enabled: !!selectedOwnerId,
    });
  const [addingLoader, setAddingLoader] = useState(false);
  const [isAddingInputEnabled, setIsAddingInputEnabled] = useState(false);
  const departmentNameInputRef = useRef("");
  const departmentNameEditRef = useRef(undefined);
  const sameSelectedData = useCallback(
    (newValue, editDelete) => {
      const selectedSameDepartmentInAllObjects = [];
      fieldValues.forEach((element, ind) => {
        if (element.DepartmentId?.id === newValue.id) {
          selectedSameDepartmentInAllObjects.push(ind);
        }
      });
      if (selectedSameDepartmentInAllObjects.length) {
        const fields = fieldValues;
        selectedSameDepartmentInAllObjects.forEach((i) => {
          if (editDelete === "delete") {
            fields[i].DepartmentId = null;
          } else {
            fields[i].DepartmentId = newValue;
          }
        });
        updateState({ fields: fields.map((res) => res) });
      }
    },
    [fieldValues]
  );
  const deleteDepartmentNameHandler = async (Id) => {
    setAddingLoader(true);
    try {
      const resp = await deleteDepartmentName({
        Id,
        OwnerId: selectedOwnerId,
      });
      if (resp.message) {
        toast.success(resp.message);
      }
      refetch();
      sameSelectedData({ id: Id }, "delete");
      if (selectedVal?.id === Id) {
        setSelectedVal(null);
      }
      if (isAddingInputEnabled) {
        setIsAddingInputEnabled(!isAddingInputEnabled);
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setAddingLoader(false);
    }
  };
  const addEditDepartmentNameHandler = async () => {
    try {
      const value = departmentNameInputRef.current.trim();
      if (!value) {
        return setError(t("staff.enterDepartmentName"));
      } else if (value.length > 100) {
        return setError(t("form.errors.maxLimit", { limit: 100 }));
      }
      setAddingLoader(true);
      const params = {
        Name: value,
        OwnerId: selectedOwnerId,
      };
      const editValueSelected = departmentNameEditRef.current;
      let resp;
      if (editValueSelected) {
        if (value === editValueSelected.name) {
          setIsAddingInputEnabled(!isAddingInputEnabled);
          departmentNameEditRef.current = undefined;
          departmentNameInputRef.current = "";
          return;
        }
        params.Id = editValueSelected.id;
        resp = await editDepartmentName(params);
        departmentNameEditRef.current = undefined;
        if (selectedVal?.id === editValueSelected.id) {
          setSelectedVal((prev) => ({
            ...prev,
            ...editValueSelected,
            name: value,
          }));
        }
        sameSelectedData(
          {
            ...preSelectedDepartement,
            name: value,
          },
          "edit"
        );
      } else {
        resp = await addDepartmentName(params);
      }
      departmentNameInputRef.current = "";
      refetch();
      if (resp.message) {
        toast.success(resp.message);
      }
      setIsAddingInputEnabled(!isAddingInputEnabled);
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setAddingLoader(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        className="modal-dialog-centered modal-width-660"
        modalClassName="custom-modal"
      >
        <span className="close-btn" onClick={() => setIsModalOpen(false)}>
          <img src={crossIcon} alt="close" />
        </span>
        <ModalBody>
          {(isLoading || isFetching || addingLoader) && <Loader />}
          <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
            <span className="modal-title-25">Select Department/Team</span>
          </Text>
          {isAddingInputEnabled ? (
            <Input
              Autofocus
              key={departmentNameEditRef.current ? "edit" : "add"}
              Title={
                departmentNameEditRef.current
                  ? "Edit Department/Team"
                  : "Add Department/Team"
              }
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: departmentNameEditRef.current
                  ? "Edit Department/Team"
                  : "Add Department/Team",
              })}
              MaxLength={100}
              Name={"departmentName"}
              HandleChange={(e) => {
                const value = e.target.value.trim();
                if (value && error) {
                  setError("");
                }
                departmentNameInputRef.current = value;
              }}
              Error={error}
              HelperLabel={
                <span
                  className="link-btn d-flex  align-items-center h-100 my-2 bg-white"
                  onClick={() => {
                    addEditDepartmentNameHandler();
                  }}
                >
                  <img
                    src={icon_tick}
                    alt="icon"
                    width={15}
                    style={{ marginRight: 3 }}
                  />
                  {departmentNameEditRef.current ? "Edit" : "Add"}
                </span>
              }
              defaultValue={departmentNameEditRef.current?.name}
            />
          ) : (
            <span
              className="link-btn"
              onClick={() => setIsAddingInputEnabled(!isAddingInputEnabled)}
            >
              <img src={plus} alt="icon" width={20} className="me-xxs" />
              Add Department/Team
            </span>
          )}
          {data?.length > 0 ? (
            <ul
              className={
                styles["change-list"] + " my-2 " + styles["modal-view-lists"]
              }
            >
              {data.map((item, key) => (
                <li key={key}>
                  <div className="ch-radio d-flex justify-content-between">
                    <label className={styles.label_width}>
                      <input
                        type="radio"
                        readOnly
                        onClick={() => setSelectedVal(item)}
                        checked={item?.id === selectedVal?.id}
                        name="blockTimeslotAallDoctors"
                      />
                      <span className="word-break">{item?.name} </span>
                    </label>
                    <div className="d-flex gap-sm">
                      <img
                        src={edit_icon}
                        alt="edit_icon"
                        width={20}
                        onClick={() => {
                          setIsAddingInputEnabled(false);
                          departmentNameInputRef.current = item.name;
                          departmentNameEditRef.current = item;
                          setTimeout(() => {
                            setIsAddingInputEnabled(true);
                          }, 200);
                        }}
                      />
                      <img
                        src={delete_icon}
                        alt="delete_icon"
                        width={20}
                        style={
                          item?.isDeletable ? {} : { cursor: "not-allowed" }
                        }
                        onClick={() => {
                          if (item?.isDeletable) {
                            deleteDepartmentNameHandler(item.id);
                          } else {
                            toast.error(t("staff.errorExistingDepartment"));
                          }
                        }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <Empty Message="No department available for your selection" />
          )}
          <button
            className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
            title={t("apply")}
            onClick={() => {
              setSelectedDepartment({ DepartmentId: selectedVal });
              setIsModalOpen(false);
              setSelectedVal(null);
            }}
          >
            {t("apply")}
          </button>
          <button
            className="button button-round button-border button-dark btn-mobile-link "
            onClick={() => {
              setIsModalOpen(false);
              setSelectedVal(null);
            }}
            title={t("cancel")}
          >
            {t("cancel")}
          </button>
        </ModalBody>
      </Modal>
    </>
  );
}

export default withTranslation()(DepartmentSelectionModal);
